/**
 * Edit Team Member Modal Component
 * Task 5.5: Create Edit Team Member Modal
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { teamMemberService } from '@/lib/api/teamMember.service';
import { getRolePermissions } from '@/utils/permissions';
import type { TeamMemberWithUser, TeamRole, TeamMemberStatus } from '@/types/teamMember.types';
import { ROLE_LABELS, ROLE_DESCRIPTIONS } from '@/types/teamMember.types';

type EditablePermissionKey =
  | 'canEditCelebration'
  | 'canManageTeam'
  | 'canManageEvents'
  | 'canManageGuests'
  | 'canAddRemoveGuests'
  | 'canManageVendors'
  | 'canManageServices'
  | 'canManageActivities'
  | 'canCreateTasks';

const ROLE_PRIORITY: TeamRole[] = ['organizer', 'event_manager', 'vendor_coordinator', 'host', 'member'];

const PERMISSION_OPTIONS: Array<{ key: EditablePermissionKey; labelKey: string }> = [
  { key: 'canEditCelebration', labelKey: 'common:team.editMember.permissions.editCelebrationDetails' },
  { key: 'canManageTeam', labelKey: 'common:team.editMember.permissions.manageTeamMembers' },
  { key: 'canManageEvents', labelKey: 'common:team.editMember.permissions.manageEvents' },
  { key: 'canManageGuests', labelKey: 'common:team.editMember.permissions.manageGuests' },
  { key: 'canAddRemoveGuests', labelKey: 'common:team.editMember.permissions.addOrRemoveGuests' },
  { key: 'canManageVendors', labelKey: 'common:team.editMember.permissions.manageVendors' },
  { key: 'canManageServices', labelKey: 'common:team.editMember.permissions.manageServices' },
  { key: 'canManageActivities', labelKey: 'common:team.editMember.permissions.manageActivities' },
  { key: 'canCreateTasks', labelKey: 'common:team.editMember.permissions.createTasks' },
];

const getClosestRoleForPermissionSelection = (
  currentRole: TeamRole,
  availableRoles: TeamRole[],
  key: EditablePermissionKey,
  checked: boolean
): TeamRole => {
  const desiredPermissions = {
    ...getRolePermissions(currentRole),
    [key]: checked,
  };

  const orderedRoles = ROLE_PRIORITY.filter((role) => availableRoles.includes(role));
  let bestRole = currentRole;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const candidateRole of orderedRoles) {
    const candidatePermissions = getRolePermissions(candidateRole);
    const mismatchCount = PERMISSION_OPTIONS.reduce((count, option) => {
      return count + (candidatePermissions[option.key] === desiredPermissions[option.key] ? 0 : 1);
    }, 0);

    if (
      mismatchCount < bestScore ||
      (mismatchCount === bestScore && candidateRole === currentRole)
    ) {
      bestRole = candidateRole;
      bestScore = mismatchCount;
    }
  }

  return bestRole;
};

interface EditTeamMemberModalProps {
  member: TeamMemberWithUser;
  celebrationId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export const EditTeamMemberModal: React.FC<EditTeamMemberModalProps> = ({
  member,
  celebrationId,
  onSuccess,
  onCancel
}) => {
  const { t } = useTranslation(['common']);
  const [selectedRole, setSelectedRole] = useState<TeamRole>(member.role);
  const [selectedStatus, setSelectedStatus] = useState<TeamMemberStatus>(member.status);
  const [celebrationQuotaInput, setCelebrationQuotaInput] = useState(
    member.celebration_guest_quota === null || member.celebration_guest_quota === undefined
      ? ''
      : String(member.celebration_guest_quota)
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roles: TeamRole[] = member.is_celebration_creator
    ? ['organizer', 'event_manager']
    : ['organizer', 'event_manager', 'host', 'vendor_coordinator', 'member'];
  const selectedRolePermissions = getRolePermissions(selectedRole);

  const handlePermissionToggle = (key: EditablePermissionKey, checked: boolean) => {
    const nextRole = getClosestRoleForPermissionSelection(selectedRole, roles, key, checked);
    setSelectedRole(nextRole);
  };

  const parseCelebrationQuota = (): number | null => {
    if (selectedRole !== 'host') {
      return null;
    }

    const trimmed = celebrationQuotaInput.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < 0) {
      throw new Error(t('common:team.editMember.errors.invalidHostQuota'));
    }

    return parsed;
  };

  const handleSubmit = async (event?: React.SyntheticEvent) => {
    event?.preventDefault();

    try {
      setSubmitting(true);
      setError(null);
      const celebrationGuestQuota = parseCelebrationQuota();

      await teamMemberService.updateTeamMember(celebrationId, member.id, {
        role: selectedRole,
        status: selectedStatus,
        celebration_guest_quota: celebrationGuestQuota,
      });

      onSuccess();
    } catch (err: any) {
      console.error('Error updating team member:', err);
      setError(err.response?.data?.error || t('common:team.editMember.errors.updateFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3 sm:px-5 sm:py-4">
          <h2 className="text-xl font-semibold">{t('common:team.editMember.title')}</h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-3 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label>{t('common:team.editMember.labels.user')}</Label>
            <div className="p-2.5 bg-gray-50 rounded-md">
              <p className="font-medium">{member.user.full_name}</p>
              <p className="text-sm text-gray-600">{member.user.email}</p>
              {member.is_celebration_creator && (
                <p className="text-xs text-slate-500 mt-2">
                  {t('common:team.editMember.creatorHint')}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">{t('common:team.editMember.labels.roleRequired')}</Label>
            <select
              id="role"
              title={t('common:team.editMember.labels.selectRoleTitle')}
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as TeamRole)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            >
              {roles.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
            {selectedRole && (
              <p className="text-sm text-gray-600">
                {ROLE_DESCRIPTIONS[selectedRole]}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('common:team.editMember.labels.permissions')}</Label>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {PERMISSION_OPTIONS.map((permission) => (
                  <label
                    key={permission.key}
                    className={`flex items-start gap-2 rounded-md border border-slate-200 bg-white px-2 py-2 text-sm ${selectedRolePermissions[permission.key] ? 'text-slate-700' : 'text-slate-500'}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRolePermissions[permission.key]}
                      onChange={(event) => handlePermissionToggle(permission.key, event.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="leading-tight">{t(permission.labelKey)}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-slate-500">
                {t('common:team.editMember.hints.permissions')}
              </p>
            </div>
          </div>

          {selectedRole === 'host' && (
            <div className="space-y-2">
              <Label htmlFor="celebration_quota">{t('common:team.editMember.labels.celebrationQuota')}</Label>
              <input
                id="celebration_quota"
                type="number"
                min={0}
                step={1}
                value={celebrationQuotaInput}
                onChange={(event) => setCelebrationQuotaInput(event.target.value)}
                placeholder={t('common:team.editMember.placeholders.celebrationQuota')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-sm text-gray-600">
                {t('common:team.editMember.hints.celebrationQuota')}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="status">{t('common:team.editMember.labels.statusRequired')}</Label>
            <select
              id="status"
              title={t('common:team.editMember.labels.selectStatusTitle')}
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as TeamMemberStatus)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="active">{t('common:status.active')}</option>
              {!member.is_celebration_creator && <option value="inactive">{t('common:status.inactive')}</option>}
            </select>
            <p className="text-sm text-gray-600">
              {selectedStatus === 'active' 
                ? t('common:team.editMember.statusHelp.active')
                : t('common:team.editMember.statusHelp.inactive')}
            </p>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={submitting}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? t('common:status.saving') : t('common:team.editMember.actions.saveChanges')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
